import React, { useState, useEffect } from 'react';
import DashboardLayout from '../layout/DashboardLayout';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProjects } from '../../store/slices/projectsSlice';
import { fetchEvents } from '../../store/slices/eventsSlice';
import Spinner from '../common/Spinner';
import ProjectDetailsDialog from './ProjectDetailsDialog';
import EventDialog from '../calendar/EventDialog';
import { useLocation } from 'react-router-dom';
import { useThemeColors } from '../../hooks/useThemeColors';

const locales = {
  'en-US': require('date-fns/locale/en-US')
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});

const ProjectCalendar = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { buttonColors } = useThemeColors();
  const { projects, loading } = useSelector(state => state.projects);
  const { events: eventData } = useSelector(state => state.events);
  const currentUserId = useSelector(state => state.auth.user._id);
  const [events, setEvents] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);

  useEffect(() => {
    if (location.pathname !== '/projects/calendar') {
      return;
    }
    
    loadData();
  }, [dispatch, location.pathname]);

  const loadData = async () => {
    try {
      await Promise.all([
        dispatch(fetchProjects({ limit: 100 })).unwrap(),
        dispatch(fetchEvents()).unwrap()
      ]);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  useEffect(() => {
    if (projects && eventData) {
      const allEvents = [
        ...projects.map(project => ({
          id: project._id,
          title: `${project.jobNumber} - ${project.client?.name || 'No Client'}`,
          start: new Date(project.startDate || project.createdAt),
          end: new Date(project.endDate || project.createdAt),
          project: project,
          isProject: true,
          color: '#3B82F6'
        })),
        ...eventData
          .filter(event => 
            event.visibility === 'all' || 
            event.creator === currentUserId || 
            (event.members && event.members.includes(currentUserId))
          )
          .map(event => ({
            id: event._id,
            title: event.title,
            start: new Date(event.start),
            end: new Date(event.end),
            allDay: event.allDay,
            event: event,
            isProject: false,
            color: event.color || '#3B82F6',
            description: `${event.description || ''}
              ${event.location ? `📍 ${event.location}` : ''}
              ${event.members?.length > 0 ? `👥 ${event.members.length} members` : ''}
            `.trim()
          }))
      ];
      setEvents(allEvents);
    }
  }, [projects, eventData, currentUserId]);

  const eventStyleGetter = (event) => {
    if (event.isProject) {
      let backgroundColor = '#3B82F6';
      
      switch (event.project.priority) {
        case 'high':
          backgroundColor = '#EF4444';
          break;
        case 'medium':
          backgroundColor = '#F59E0B';
          break;
        case 'low':
          backgroundColor = '#10B981';
          break;
        default:
          break;
      }

      return {
        style: {
          backgroundColor,
          borderRadius: '4px',
          opacity: 0.8,
          color: 'white',
          border: 'none',
          display: 'block',
          '--event-color': backgroundColor
        }
      };
    }
    
    const backgroundColor = event.color || '#3B82F6';
    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.8,
        color: 'white',
        border: 'none',
        display: 'block',
        '--event-color': backgroundColor
      }
    };
  };

  const handleEventClick = (calendarEvent) => {
    if (calendarEvent.isProject) {
      setSelectedProject(calendarEvent.project);
      setIsDetailsOpen(true);
    } else if (calendarEvent.event) {
      setSelectedEvent(calendarEvent.event);
      setIsEventDialogOpen(true);
    }
  };

  const handleSelect = ({ start, end }) => {
    setSelectedEvent(null);
    setIsEventDialogOpen(true);
  };

  if (location.pathname !== '/projects/calendar') {
    return null;
  }

  return (
    <DashboardLayout hideGreyMenu={true}>
      <div className="p-4">
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Calendar</h2>
            <button
              onClick={() => {
                setSelectedEvent(null);
                setIsEventDialogOpen(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{
                backgroundColor: buttonColors.primary,
                '--tw-ring-color': buttonColors.primary,
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = buttonColors.primaryHover}
              onMouseLeave={(e) => e.target.style.backgroundColor = buttonColors.primary}
            >
              <svg 
                className="-ml-1 mr-2 h-5 w-5" 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 20 20" 
                fill="currentColor"
              >
                <path 
                  fillRule="evenodd" 
                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" 
                  clipRule="evenodd" 
                />
              </svg>
              Create Event
            </button>
          </div>

          <div className="p-4" style={{ height: 'calc(100vh - 180px)' }}>
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <Spinner size="xl" color="blue" />
              </div>
            ) : (
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%' }}
                eventPropGetter={eventStyleGetter}
                onSelectEvent={handleEventClick}
                onSelectSlot={handleSelect}
                selectable
                views={['month', 'week', 'day']}
                defaultView="month"
                popup
                className="custom-calendar"
                components={{
                  event: CustomEvent
                }}
              />
            )}
          </div>
        </div>
      </div>

      {selectedProject && (
        <ProjectDetailsDialog
          isOpen={isDetailsOpen}
          onClose={() => {
            setIsDetailsOpen(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
        />
      )}

      <EventDialog
        isOpen={isEventDialogOpen}
        onClose={() => {
          setIsEventDialogOpen(false);
          setSelectedEvent(null);
        }}
        event={selectedEvent}
      />
    </DashboardLayout>
  );
};

const CustomEvent = ({ event }) => {
  if (event.isProject) {
    return (
      <div className="p-2 h-full">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
          </svg>
          <div className="font-semibold truncate">{event.title}</div>
        </div>
        <div className="text-xs opacity-75 mt-1 flex items-center gap-1">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-white/20">
            Project
          </span>
          {event.project.priority && (
            <span className={`
              inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium
              ${event.project.priority === 'high' ? 'bg-red-500/20' : 
                event.project.priority === 'medium' ? 'bg-yellow-500/20' : 
                'bg-green-500/20'}
            `}>
              {event.project.priority.charAt(0).toUpperCase() + event.project.priority.slice(1)}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 h-full">
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
        </svg>
        <div className="font-semibold truncate">{event.title}</div>
      </div>
      {event.description && (
        <div className="mt-1 space-y-1">
          {event.event.description && (
            <div className="text-xs opacity-90 line-clamp-2">
              {event.event.description}
            </div>
          )}
          <div className="flex flex-wrap gap-1">
            {event.event.location && (
              <span className="inline-flex items-center gap-1 text-xs bg-white/20 px-1.5 py-0.5 rounded-full">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                {event.event.location}
              </span>
            )}
            {event.event.members?.length > 0 && (
              <span className="inline-flex items-center gap-1 text-xs bg-white/20 px-1.5 py-0.5 rounded-full">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
                {event.event.members.length}
              </span>
            )}
            {event.event.allDay && (
              <span className="inline-flex items-center gap-1 text-xs bg-white/20 px-1.5 py-0.5 rounded-full">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                All Day
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCalendar; 